import { Injectable } from '@angular/core';
import { TrimConfiguration } from './models/trim-config.model';
import { StoryModel } from '../story/models/story.model';
import { Store } from '@ngxs/store';
import { StoryState } from '../story/state/story.state';
import { StoryChaptersState } from '../story/state/chapters/story.chapters.state';
import { SerializeChapterService } from './serialize/chapter/serialize-chapter.service';
import { serializeGraph } from './serialize/scene-graph/serialize-graph';
import { cloneDeep, isEmpty } from 'lodash';
import { ChapterModel } from '../story/models/chapter.model';
import { EngineService } from '../engine/engine.service';
import { BdGraphPropertyValueMap } from 'src/lib/graph/graph.model';
import { updateParticleSystems } from '../story/merge/merge-particle-systems';
import { StoryAnimationModel } from '../story/models/story.model';
import { AnimationService } from '../engine/animation.service';

@Injectable({
  providedIn: 'root'
})
export class SerializeModuleService {

  constructor(
    private store: Store,
    private engineService: EngineService,
    private chapterSerializer: SerializeChapterService,
    private animationService: AnimationService
  ) {}

  private serializeUI(story: StoryModel) {

    const ui = { ...story.ui };
    if (story.chapters.length > 1) {
      ui.scrubber = false;
      ui.playPause = false;
      ui.tour = { chapters: true };
    }
    return ui;

  }

  // #NOTE: [EM] This shouldn't be necessary. The user should be able to explicitly state their intention.
  // programatically create the animation property based on the currently active chapter's timeline data
  private serializeAnimation(zeroethChapter: ChapterModel) {
    // is there data in the current chapter's timeline map?
    const zeroethChapterAnimated = this.animationService.isChapterAnimated(zeroethChapter);
  
    if (zeroethChapterAnimated) {
      return {
        startTime: zeroethChapter.timelineMap[0].start,
        loop: zeroethChapter.loop || true,
        numChapters: 1
      };
    }
    
    return {};
  }

  // creates a map of objects to trim from the scene graph
  private generateTrimObjectMap(chapters: ChapterModel[], trimConfig: TrimConfiguration): BdGraphPropertyValueMap {
    // use serialized chapters to query scene-graph for trimmable objects
    let shownObjectState = {};
    if (trimConfig.graph.hidden) {
      // #NOTE: [EM] This could be extended to include otehr 'shown' conditions e.g. clipped, opacity, etc.
      shownObjectState = {...shownObjectState, ...this.engineService.queryShownObjects(chapters, true)};
    }

    // invert shown object state for trim object state
    const trimObjectState = {};
    // tslint:disable-next-line:forin
    for (const id in shownObjectState) {
      trimObjectState[id] = !shownObjectState[id];
    }
    return trimObjectState;
  }

  // reidentify the states in stateId property
  private serializeStateId(stateId: string | string[], namespaceMap: {[original: string]: string}): string[] {

    // handle the unfortunate case where stateId is a string, instead of a string[]
    if (typeof stateId === 'string') {
      return [namespaceMap[stateId] || stateId];
    }

    return stateId.map(oldState => {
      return namespaceMap[oldState] || oldState;
    });
  }

  // serialize the data in engine state for storage on BD server
  serialize(
    inplace: boolean = false, 
    namespaceMap: {[key: string]: string}, 
    trimConfig: TrimConfiguration,
    replaceObjectId: boolean = false,
    sourceStory: StoryModel,
    sourceChapters: ChapterModel[]
  ): {story: StoryModel, chapterIdMap: any} {
    const trimMap = this.generateTrimObjectMap(sourceChapters, trimConfig);

    // create a copy of the source story (the current story in state)
    const moduleData = cloneDeep(sourceStory);

    // replace storychapters with serialized chapters
    const serialized = this.chapterSerializer.serialize(sourceChapters, inplace, namespaceMap, replaceObjectId);
    moduleData.chapters = serialized.chapters;


    // if chapter Ids are changing, I need to re-make particle system Ids.
    if (inplace === false) {
      const customData = sourceStory.customData || {};
      const particleSystems = customData.particleSystems || [];
      if (particleSystems.length) {
        const update = updateParticleSystems(moduleData.chapters, particleSystems);
        moduleData.chapters = update.chapters;
        moduleData.customData = { 
          ...moduleData.customData, 
          particleSystems: update.particleSystemsForCustomData
        };
      }
    }


    // replace story graph with serialized graph
    moduleData.sceneGraph = serializeGraph(namespaceMap, trimMap, true, replaceObjectId);

    // replace UI configuration for widget
    moduleData.ui = this.serializeUI(moduleData);

    // replace stateId with namespace mapped ids
    moduleData.stateId = this.serializeStateId(moduleData.stateId, namespaceMap);

    // #NOTE: [EM] this is a hard overwrite, which will cause problems for BD content creators
    moduleData.animation = this.serializeAnimation(sourceChapters[0]) as StoryAnimationModel;
    
    return {
      story: moduleData,
      chapterIdMap: serialized.sourceIdMap
    };
  }
}
